import { supabase } from '@/services/supabase/client';
import env from '@/utils/env';
import { assembleSystemPrompt } from '@/services/ask-sanjoy/prompts';
import type { KnowledgeChunk, GraphPathNode } from '@/services/ask-sanjoy/prompts';
import { reportKnowledgeGap } from '@/services/search';

export interface ConversationContext {
  industry?: string;
  goal?: string;
  article?: string;
  pageUrl?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface Conversation {
  id: string;
  session_id: string | null;
  user_id: string | null;
  current_industry: string | null;
  business_goal: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown>;
}

export interface RetrievedContext {
  chunks: KnowledgeChunk[];
  hasEmbedding: boolean;
}

export async function createConversation(
  context: ConversationContext,
  sessionId: string,
  userId?: string
): Promise<string | null> {
  const { data, error } = await supabase
    .from('conversations')
    .insert({
      session_id: sessionId,
      user_id: userId || null,
      current_industry: context.industry || null,
      business_goal: context.goal || null,
      status: 'active',
      metadata: {
        initial_context: context,
        started_from: context.pageUrl || '/ask',
      },
    })
    .select('id')
    .single();

  if (error) {
    console.error('Failed to create conversation:', error);
    return null;
  }

  return data.id;
}

export async function saveUserMessage(
  conversationId: string,
  content: string,
  metadata?: Record<string, unknown>
): Promise<string | null> {
  const { data, error } = await supabase
    .from('conversation_messages')
    .insert({
      conversation_id: conversationId,
      role: 'user',
      content,
      metadata: metadata || {},
    })
    .select('id')
    .single();

  if (error) {
    console.error('Failed to save user message:', error);
    return null;
  }

  return data.id;
}

export async function retrieveContext(
  query: string,
  context?: ConversationContext
): Promise<RetrievedContext> {
  try {
    const response = await fetch(
      `${env.supabaseUrl}/functions/v1/ask-sanjoy-retrieve`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${env.supabaseAnonKey}`,
        },
        body: JSON.stringify({
          query,
          context: context
            ? {
                industry: context.industry || undefined,
                goal: context.goal || undefined,
                article: context.article || undefined,
              }
            : undefined,
          limit: 5,
        }),
      }
    );

    if (!response.ok) {
      return { chunks: [], hasEmbedding: false };
    }

    const data = await response.json();
    return {
      chunks: (data.chunks ?? []).map((c: Record<string, unknown>) => ({
        title: c.title as string,
        summary: c.summary as string,
        entityType: c.entity_type as string,
        slug: c.slug as string,
        relevanceScore: c.relevance_score as number,
      })),
      hasEmbedding: data.has_embedding ?? false,
    };
  } catch {
    return { chunks: [], hasEmbedding: false };
  }
}

export interface StreamCallbacks {
  onToken: (token: string) => void;
  onDone: (fullContent: string) => void;
  onError: (error: string) => void;
}

export async function streamChatResponse(
  messages: Array<{ role: 'user' | 'assistant'; content: string }>,
  context: ConversationContext,
  knowledgeChunks: KnowledgeChunk[],
  conversationId: string | null,
  sessionId: string,
  callbacks: StreamCallbacks,
  graphPaths?: GraphPathNode[]
): Promise<void> {
  const systemPrompt = assembleSystemPrompt({
    industry: context.industry,
    goal: context.goal,
    article: context.article,
    pageUrl: context.pageUrl,
    knowledgeChunks,
    graphPaths,
  });

  try {
    const response = await fetch(
      `${env.supabaseUrl}/functions/v1/ask-sanjoy`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${env.supabaseAnonKey}`,
        },
        body: JSON.stringify({
          messages,
          context,
          conversation_id: conversationId,
          session_id: sessionId,
          system_prompt: systemPrompt,
        }),
      }
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      callbacks.onError(err.error || 'Failed to get response');
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError('No response stream');
      return;
    }

    const decoder = new TextDecoder();
    let fullContent = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (!data) continue;

        try {
          const parsed = JSON.parse(data);

          if (parsed.error) {
            callbacks.onError(parsed.error);
            return;
          }

          if (parsed.done) {
            fullContent = parsed.full_content || fullContent;
            callbacks.onDone(fullContent);
            return;
          }

          if (parsed.token) {
            fullContent += parsed.token;
            callbacks.onToken(parsed.token);
          }
        } catch {
          // Skip malformed chunks
        }
      }
    }

    // If we exit the loop without done signal
    if (fullContent) {
      callbacks.onDone(fullContent);
    }
  } catch (err) {
    callbacks.onError(err instanceof Error ? err.message : 'Stream failed');
  }
}

export async function getUserConversations(
  userId: string,
  limit = 20
): Promise<Conversation[]> {
  const { data, error } = await supabase
    .from('conversations')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
    .limit(limit);

  if (error) return [];
  return (data ?? []) as Conversation[];
}

export async function getConversationMessages(
  conversationId: string
): Promise<ChatMessage[]> {
  const { data, error } = await supabase
    .from('conversation_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });

  if (error) return [];
  return (data ?? []) as ChatMessage[];
}

export async function updateConversationContext(
  conversationId: string,
  updates: {
    industry?: string;
    goal?: string;
    metadata?: Record<string, unknown>;
  }
): Promise<void> {
  const updatePayload: Record<string, unknown> = {
    updated_at: new Date().toISOString(),
  };

  if (updates.industry) updatePayload.current_industry = updates.industry;
  if (updates.goal) updatePayload.business_goal = updates.goal;
  if (updates.metadata) updatePayload.metadata = updates.metadata;

  await supabase
    .from('conversations')
    .update(updatePayload)
    .eq('id', conversationId);
}

export async function handleKnowledgeGap(
  query: string,
  userId?: string
): Promise<void> {
  await reportKnowledgeGap(query, userId);
}

export function generateSessionId(): string {
  const stored = sessionStorage.getItem('sj-session-id');
  if (stored) return stored;

  const id = crypto.randomUUID();
  sessionStorage.setItem('sj-session-id', id);
  return id;
}
